import React from 'react'
import { ProgressCircle } from '../ProgressCircle/ProgressCircle'
import { Div, Flex, Separator, Text } from '@sendoutcards/quantum-design-ui'
import {
  AffiliateBonusCard,
  AffiliateBonusCardProps,
} from './components/AffiliateBonusCard'
import { useContainerWidth, useQueries, useSelector, useState } from 'src/hooks'
import { CountDownPill } from './components/CountDownPill'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import {
  BonusInfoModal,
  BonusInfoModalProps,
} from './components/BonusInfoModal'
import {
  getAffiliateBonusInfo,
  getDoubleBonusReports,
} from 'src/legacy_graphql'
import HoverTooltip from 'src/chrome/HoverTooltip/HoverTooltip'
import { GradientText } from './components/StyledText'
import { Button } from 'src/design_system/components/Button/Button'
import {
  GlassBackgroundContainer,
  GlassBlobType,
} from 'src/pricing_page/components/GlassBackground'

export const AffiliateBonusWidget = () => {
  const [reports, marketingContent] = useQueries(
    getDoubleBonusReports(),
    getAffiliateBonusInfo(),
  )
  const [containerRef, containerWidth] = useContainerWidth()
  const screenWidth = useSelector(state => state.window.width)
  const [isOpen, setIsOpen] = useState(false)
  const [info, setInfo] = useState<BonusInfoModalProps | null>(null)

  const singleBonusDescription = marketingContent.singleBonusInfo.content
  const doubleBonusDescription = marketingContent.doubleBonusInfo.content
  const pqvInfoDescrittion = marketingContent.pqvInformation.content

  const isContainerBreakPoint = containerWidth <= 650
  const isMobile = screenWidth <= 428
  const shouldStack = isContainerBreakPoint ? 'column' : undefined
  const [currentReport] = reports.doubleBonusReports
  const isDoubleBonusEligible =
    parseInt(currentReport.personalQualifyingVolume, 10) >= 4000

  const gradientText = {
    background: 'linear-gradient(74deg, #34E3DF 16.27%, #02AEEE 86.38%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  }

  const trimString = (value: string) => {
    const numericString = value.replace(/[^\d.]/g, '')
    const indexOfDecimal = numericString.indexOf('.')
    const trimmedValue =
      indexOfDecimal !== -1
        ? parseInt(numericString.slice(0, indexOfDecimal), 10)
        : parseInt(numericString, 10)
    return trimmedValue
  }

  const getBonus = (multiplier: number) => {
    const BASE_DENOMINATOR = 4000
    const bonusPercentage =
      parseInt(currentReport.personalQualifyingVolume, 10) /
      (BASE_DENOMINATOR * multiplier)

    const roundedBonus = Math.round(bonusPercentage * 100)

    return {
      roundedBonus,
    }
  }

  const affiliateCommision = trimString(
    currentReport.earnedAffiliateCommissionsMonthToDate,
  )
  const singleBonusPercentage = getBonus(1).roundedBonus
  const doubleBonusPercentage = getBonus(2).roundedBonus
  const singleBonusCommission = affiliateCommision * 0.5

  const accumulatedQV = `${trimString(currentReport.personalQualifyingVolume)}`

  const currentText = (
    <GradientText
      fontSize={'14px'}
      fontWeight={500}
      color={gradientText.background}
    >
      {accumulatedQV}
    </GradientText>
  )

  const getDaysLeftInMonth = () => {
    const now = new Date()
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return endOfMonth.getDate() - now.getDate()
  }

  const getCurrentMonthAndYear = () => {
    const now = new Date()
    const options: Intl.DateTimeFormatOptions = {
      month: 'long',
      year: 'numeric',
    }
    return now.toLocaleDateString(undefined, options)
  }

  const bonusFactory = (
    title: string,
    description: string,
    multiplier: number,
    outstandingQV: string,
    total: string,
    bonusTotal: number,
    grandTotal: number,
  ) => {
    return {
      title: title,
      description: description,
      earnedQV: accumulatedQV,
      outstandingQV: `${trimString(outstandingQV)}`,
      totalQV: total,
      percent: getBonus(multiplier).roundedBonus,
      totalCommision: `$${affiliateCommision}`,
      grandTotal: `$${grandTotal}`,
      bonusTotal: `$${bonusTotal}`,
      onClose: () => setIsOpen(false),
    }
  }

  const singleBonusInfo = bonusFactory(
    '50% bonus',
    singleBonusDescription,
    1,
    currentReport.additionalPqvNeededToHit50PercentBonus,
    '4000',
    singleBonusCommission,
    affiliateCommision + singleBonusCommission,
  )
  const doubleBonusInfo = bonusFactory(
    'Double Bonus',
    doubleBonusDescription,
    2,
    currentReport.additionalPqvNeededToHit100PercentBonus,
    '8000',
    affiliateCommision,
    affiliateCommision * 2,
  )

  const singleBonusCommissions = [
    { title: 'Affiliate Commissions', total: affiliateCommision },
    { title: '50% Bonus', total: singleBonusCommission },
  ]

  const doubleBonusCommisions = [
    { title: 'Affiliate Commissions', total: affiliateCommision },
    { title: 'Double Bonus', total: affiliateCommision },
  ]

  const bonusCardData: AffiliateBonusCardProps[] = [
    {
      earnedQV: accumulatedQV,
      onClick: () => {
        setInfo(singleBonusInfo)
        setIsOpen(true)
      },
      percent: singleBonusPercentage,
      bonus: '50%',
      totalCommision: `$${singleBonusCommission + affiliateCommision}`,
      isEligible: true,
      pqvOutstanding: `${trimString(singleBonusInfo.outstandingQV)}`,
      commissions: singleBonusCommissions,
    },
    {
      earnedQV: accumulatedQV,
      onClick: () => {
        setInfo(doubleBonusInfo)
        setIsOpen(true)
      },
      percent: doubleBonusPercentage,
      bonus: 'Double',
      totalCommision: `$${affiliateCommision * 2}`,
      isEligible: isDoubleBonusEligible,
      pqvOutstanding: `${trimString(doubleBonusInfo.outstandingQV)}`,
      commissions: doubleBonusCommisions,
    },
  ]

  const blobs: GlassBlobType[] = [
    {
      size: '30%',
      shape: 'circle',
      filter: 'blur(60px)',
      offset: {
        top: '10%',
        left: '-15%',
      },
      gradient:
        'linear-gradient(to bottom right, #b9eef366 0%, #bfd57366 20%, #35c47466 32%, #b9eef366 50%)',
    },
    {
      size: '40%',
      shape: 'circle',
      filter: 'blur(50px)',
      offset: {
        top: '-10%',
        left: '5%',
      },
      gradient:
        'linear-gradient(to bottom left, #b9eef366 0%, #bfd57366 20%, #35c47466 32%, #b9eef366 50%) bottom left / 50% 50% no-repeat',
    },
    {
      size: '40%',
      shape: 'circle',
      filter: 'blur(50px)',
      offset: {
        top: '5%',
        left: '15%',
      },
      gradient:
        'linear-gradient(to top left, #b9eef366 0%, #bfd57366 20%, #35c47466 32%, #b9eef366 50%) top left / 50% 50% no-repeat',
    },
    {
      size: '40%',
      shape: 'circle',
      filter: 'blur(50px)',
      offset: {
        top: '5%',
        left: '15%',
      },
      gradient:
        'linear-gradient(to top right, #b9eef366 0%, #bfd57366 20%, #35c47466 32%, #b9eef366 50%) top right / 50% 50% no-repeat',
    },
  ]

  return (
    <Div
      position="relative"
      ref={containerRef}
      display="flex"
      inset={{ horizontal: '1.5rem', top: '1.5rem', bottom: '.75rem' }}
      backgroundColor="#FFF"
      style={{
        borderRadius: '1rem',
        boxShadow: '0px 0px 32px 0px rgba(0, 0, 0, 0.12)',
      }}
    >
      {info && isOpen && (
        <Flex
          alignItems="center"
          width="100%"
          height="100%"
          backgroundColor="#bebebe54"
          zIndex={6}
          position="absolute"
          left="0px"
          top="0px"
          inset={{ horizontal: '1rem' }}
          style={{
            backdropFilter: 'blur(8px)',
            borderRadius: 'inherit',
          }}
        >
          <BonusInfoModal {...info} />
        </Flex>
      )}
      <GlassBackgroundContainer blobStart="center" blobs={blobs}>
        <Flex
          flexDirection="column"
          rowGap="x3"
          width="100%"
          alignItems="center"
        >
          <Flex columnGap="x3" width="100%" flexDirection={shouldStack}>
            <Flex flexDirection="column" alignItems="center">
              <Flex flexDirection="row" columnGap="x1_5" alignItems="center">
                <Text
                  content="Overall Progress"
                  type="body"
                  lineHeight={1.2}
                  style={{
                    fontSize: isMobile ? '.825rem' : '.925rem',
                    fontWeight: 500,
                    fontFamily: 'Montserrat, san serif',
                    color: '#6B7280',
                  }}
                />
                <HoverTooltip
                  title={'Affiliate Comission month to date.'}
                  shouldDisplayTooltip={true}
                  isFullWidth={true}
                  background="dark"
                >
                  <Icon name="info" size={16} color="#6B7280" />
                </HoverTooltip>
              </Flex>
              <Text
                content={getCurrentMonthAndYear()}
                type="body"
                alignment={isContainerBreakPoint ? 'center' : 'left'}
                inset={{ bottom: isContainerBreakPoint ? 'x1_5' : 'x0' }}
                style={{
                  fontSize: isMobile ? '.825rem' : '.925rem',
                  fontWeight: 500,
                  fontFamily: 'Montserrat, san serif',
                  color: '#171717',
                }}
              />
              <Flex
                position="relative"
                inset={{
                  horizontal: 'x2',
                  top: isContainerBreakPoint ? 'x0' : 'x2',
                }}
                flexDirection="column"
                alignItems="center"
                minWidth="210px"
              >
                <Div
                  display="flex"
                  position="absolute"
                  top={isContainerBreakPoint ? '-46px' : '-22px'}
                  right={isContainerBreakPoint ? undefined : '30px'}
                  flexDirection="column"
                  rowGap="x1"
                >
                  <ProgressCircle
                    percent={doubleBonusPercentage}
                    description={`${accumulatedQV}/8000 PQV`}
                  />
                  <Text
                    type="body"
                    alignment="center"
                    whiteSpace={isContainerBreakPoint ? 'normal' : 'pre'}
                    lineHeight={1.5}
                    style={{
                      fontSize: isMobile ? '.725rem' : '.825rem',
                      fontWeight: 500,
                      fontFamily: 'Montserrat, san serif',
                      color: '#6B7280',
                    }}
                    content={'Earned Affiliate\n Comissions'}
                  />
                  <Text
                    content={`$${affiliateCommision}`}
                    type="body"
                    style={{
                      fontSize: '20px',
                      fontWeight: 700,
                      fontFamily: 'Montserrat, san serif',
                      color: '#404040',
                    }}
                    alignment="center"
                  />
                  <Text
                    type="body"
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      fontFamily: 'Montserrat, san serif',
                      color: '#404040',
                    }}
                    alignment="center"
                  >
                    Current PQV: {currentText}
                  </Text>
                </Div>
              </Flex>
            </Flex>
            <Flex
              outset={{ top: isContainerBreakPoint ? '275px' : '' }}
              justifyContent="center"
              flexDirection="column"
              rowGap={'x1_5'}
              flexGrow={1}
            >
              <Flex
                width="100%"
                justifyContent="space-between"
                alignItems="center"
              >
                <Text
                  content="Double Bonus Program"
                  type="body"
                  style={{
                    fontSize: isMobile ? '.875rem' : '1.5rem',
                    fontWeight: 700,
                    fontFamily: 'Montserrat, san serif',
                    color: '#404040',
                    letterSpacing: 0.5,
                  }}
                />
                <CountDownPill timeLeft={getDaysLeftInMonth()} />
              </Flex>
              <Text
                content="Additional Bonus Opportunity on Affiliate Commissions"
                type="body"
                lineHeight={1.5}
                style={{
                  fontSize: isMobile ? '.725rem' : '.825rem',
                  fontWeight: 500,
                  fontFamily: 'Montserrat, san serif',
                  color: '#6B7280',
                }}
                alignment="left"
              />
              {bonusCardData.map((data, index) => {
                return (
                  <>
                    <AffiliateBonusCard key={index} {...data} />
                    {index === 0 && (
                      <Separator orientation="horizontal" caption="Or" />
                    )}
                  </>
                )
              })}
            </Flex>
          </Flex>
          <HoverTooltip
            title={pqvInfoDescrittion}
            shouldDisplayTooltip={true}
            isFullWidth={true}
            background="dark"
            tooltipDirection="top"
          >
            <Button
              title="What is PQV?"
              textColor="#404040"
              textSize="12px"
              backgroundColor="#F3F4F6"
              borderRadius="54px"
              padding="8px 12px"
              icon={{ name: 'info', size: 16, color: '#404040' }}
            />
          </HoverTooltip>
        </Flex>
      </GlassBackgroundContainer>
    </Div>
  )
}
